/**
 * Tenant admin strings.
 *
 * English is the source of truth: `StringKey` derives from it, and the
 * completeness check at the foot of this file makes a missing translation a
 * compile error rather than something an operator in Kinshasa discovers.
 *
 * LINGALA IS A DRAFT AND NEEDS A NATIVE-SPEAKER REVIEW. Same register and the
 * same borrowed-French admin vocabulary as the platform console's dictionary, so
 * the two products read alike to the same person.
 */

export const LOCALES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fr', label: 'Fran\u00e7ais', short: 'FR' },
  { code: 'sw', label: 'Kiswahili', short: 'SW' },
  { code: 'ln', label: 'Ling\u00e1la', short: 'LN' },
] as const;

export type LocaleCode = (typeof LOCALES)[number]['code'];
export type StringKey = keyof (typeof dict)['en'];

export const dict = {
  en: {
    'se.paxLanguage': 'Passenger language',
    'se.paxLangHint': 'The language your booking site opens in. Passengers can still change it themselves. Leave on “Follow their device” to use whatever their phone is set to.',
    'se.followDevice': 'Follow their device',
    'db.allTimesIn': '{date} · all times in {tz}',
    'tt.subtitle': 'Recurring schedules. Times are local to {tz}.',
    'trips.subtitle': 'Departures on {date}, in {tz}.',
    'f.cancel': 'Cancel',
    'f.timezone': 'Timezone',
    'f.currency': 'Currency',
    'f.invoices': 'Invoices',
    'brand.ops': 'BusBook operations',
    'f.status': 'Status',
    'f.date': 'Date',
    'f.route': 'Route',
    'f.bus': 'Bus',
    'f.note': 'Note',
    'f.departure': 'Departure',
    'f.departs': 'Departs',
    'f.booked': 'Booked',
    'f.reference': 'Reference',
    'f.passenger': 'Passenger',
    'f.passengers': 'Passengers',
    'f.trip': 'Trip',
    'f.seats': 'Seats',
    'f.total': 'Total',
    'f.paid': 'Paid',
    'f.value': 'Value',
    'f.opened': 'Opened',
    'f.booking': 'Booking',
    'f.journey': 'Journey',
    'f.payment': 'Payment',
    'f.provider': 'Provider',
    'f.search': 'Search',
    'f.clear': 'Clear',
    'f.all': 'All',
    'f.reply': 'Reply',
    'f.resolve': 'Resolve',
    'f.close': 'Close',
    'f.reject': 'Reject',
    'f.callingAt': 'Calling at',
    'f.operations': 'Operations',
    'f.conversation': 'Conversation',
    'f.resolution': 'Resolution',
    'f.request': 'Request',
    'st.scheduled': 'Scheduled',
    'st.delayed': 'Delayed',
    'st.departed': 'Departed',
    'st.cancelled': 'Cancelled',
    'st.confirmed': 'Confirmed',
    'st.awaiting': 'Awaiting payment',
    'st.refunded': 'Refunded',
    'st.expired': 'Expired',
    'st.open': 'Open',
    'st.inReview': 'In review',
    'st.closed': 'Closed',
    'trips.delayBy': 'Delay by (minutes)',
    'trips.noteForPax': 'Note for passengers',
    'trips.noteHint': 'Shown on their ticket, e.g. “Departs from Gate 4”.',
    'trips.oneOff': 'One-off departure',
    'trips.addOneOff': 'Add one-off departure',
    'trips.departureTime': 'Departure time',
    'trips.noteOptional': 'Optional. Shown to passengers, e.g. “Departs from Gate 4”.',
    'trips.emptyTitle': 'No departures on this date',
    'trips.emptyBody': 'Change the date, or check that a timetable covers this day.',
    'trips.manifest': 'Passenger manifest',
    'trips.manifestEmpty': 'No confirmed passengers yet.',
    'trips.applyDelay': 'Apply delay',
    'trips.sendNote': 'Send note',
    'trips.cancelDep': 'Cancel this departure',
    'trips.cancelHint': 'Passengers are notified and refunded automatically.',
    'trips.create': 'Create departure',
    'trips.chooseRoute': 'Choose a route',
    'trips.chooseBus': 'Choose a bus',
    'bk.searchHint': 'Search by reference, email, name or passenger.',
    'bk.searchPh': 'RF-8K3D2, name or email',
    'bk.openRequest': 'Open request',
    'bk.commission': 'Platform commission',
    'bk.refund': 'Refund and release seats',
    'bk.noRefundRole': 'Your role cannot issue refunds.',
    'dp.subtitle': 'Refund requests and complaints from passengers.',
    'dp.replyTo': 'Reply to the passenger',
    'dp.resolutionNote': 'Resolution note',
    'dp.sentToPax': 'Sent to the passenger.',
    'dp.emptyBody': 'Requests passengers raise about their bookings appear here.',
    'dp.bookingStatus': 'Booking status',
    'dp.markInReview': 'Mark in review',
    'dp.approveRefund': 'Approve refund',
    'f.name': 'Name',
    'f.city': 'City',
    'f.address': 'Address',
    'f.stop': 'Stop',
    'f.stops': 'Stops',
    'f.routes': 'Routes',
    'f.duration': 'Duration',
    'f.timetables': 'Timetables',
    'f.fares': 'Fares',
    'f.save': 'Save',
    'f.from': 'From',
    'f.until': 'Until',
    'f.role': 'Role',
    'f.email': 'Email',
    'f.password': 'Password',
    'f.type': 'Type',
    'f.period': 'Period',
    'f.invoice': 'Invoice',
    'f.layout': 'Layout',
    'f.plate': 'Plate',
    'f.rows': 'Rows',
    'f.columns': 'Columns',
    'f.runs': 'Runs',
    'f.valid': 'Valid',
    'f.trips': 'Trips',
    'f.buses': 'Buses',
    'f.show': 'Show',
    'f.none': 'None',
    'f.publish': 'Publish',
    'f.font': 'Font',
    'f.menu': 'Menu',
    'f.text': 'Text',
    'f.logo': 'Logo',
    'f.colours': 'Colours',
    'f.primary': 'Primary',
    'f.secondary': 'Secondary',
    'f.background': 'Background',
    'f.surface': 'Surface',
    'nw.subtitle': 'Where your buses call, and the order they call in.',
    'nw.nameHint': 'What passengers will see, e.g. “Central Station”.',
    'nw.addrHint': 'Optional. Helps passengers find the pickup point.',
    'nw.routeName': 'Route name',
    'nw.routeHint': 'e.g. “Lagos → Abuja express”.',
    'nw.noRoutes': 'No routes yet',
    'nw.noStops': 'No stops yet',
    'nw.addStopsHint': 'Add the places your buses pick up and set down.',
    'nw.addStop': 'Add stop',
    'nw.newRoute': 'New route',
    'nw.saveStop': 'Save stop',
    'nw.showToPax': 'Show to passengers',
    'nw.saveRoute': 'Save route',
    'nw.stopsInOrder': 'Stops in order',
    'nw.chooseStop': 'Choose a stop',
    'nw.saveFares': 'Save fares',
    'nw.fromTo': 'From \\ To',
    'fl.subtitle': 'Your buses and the seat layouts they use.',
    'fl.nameHint': 'What your staff call it, e.g. “Coach 12”.',
    'fl.plateNumber': 'Plate number',
    'fl.seatLayout': 'Seat layout',
    'fl.grid': 'Grid',
    'fl.usedBy': 'Used by',
    'fl.noBuses': 'No buses yet',
    'fl.noLayouts': 'No seat layouts yet',
    'fl.layoutHint': 'A layout describes where the seats, aisles and doors are on a bus.',
    'fl.newLayout': 'New layout',
    'fl.addBus': 'Add bus',
    'fl.seatLayouts': 'Seat layouts',
    'fl.saveBus': 'Save bus',
    'fl.chooseLayout': 'Choose a layout',
    'fl.inService': 'In service',
    'fl.saveLayout': 'Save layout',
    'fl.seatTiers': 'Seat tiers',
    'fl.newTier': 'New tier',
    'fl.tierName': 'Name',
    'fl.markup': 'Markup',
    'fl.tierSeatCount': 'Seats',
    'fl.saveTier': 'Save tier',
    'fl.deleteTier': 'Delete',
    'fl.defaultTierHint': 'The default tier cannot be deleted.',
    'fl.tierInUseHint': 'Repaint these seats to a different tier first.',
    'tt.busClash': 'This bus is already scheduled',
    'tt.blankHint': 'Leave blank to run indefinitely.',
    'tt.noTimetables': 'No timetables yet',
    'tt.newTimetable': 'New timetable',
    'tt.saveTimetable': 'Save timetable',
    'tt.savedAnyway': 'Saved anyway — check the bus can make both runs, or change the bus.',
    'tt.runsOn': 'Runs on',
    'tt.activeHint': 'Active — generate departures from this schedule',
    'se.subtitle': 'How your booking site behaves for passengers.',
    'se.tzHint': 'All departure times you enter and passengers see are in this zone.',
    'se.cutoff': 'Cancellation cutoff (hours)',
    'se.cutoffHint': 'Passengers can cancel themselves up to this long before departure.',
    'se.payWindow': 'Payment window (minutes)',
    'se.payHint': 'Unpaid bookings expire and seats are released.',
    'se.reminder': 'Reminder lead (minutes)',
    'se.between': 'Between 15 and 1440.',
    'se.maxSeats': 'Max seats per booking',
    'se.supportEmail': 'Support email',
    'se.supportHint': 'Shown on your Help page, and used as the reply-to on notification emails.',
    'se.supportPhone': 'Support phone',
    'se.about': 'About your company',
    'se.faq': 'FAQ',
    'se.saveSettings': 'Save settings',
    'se.locale': 'Locale',
    'se.policy': 'Booking policy',
    'se.supportContact': 'Support contact',
    'se.helpContent': 'Help page content',
    'sf.subtitle': 'Who can sign in to this account, and what they can do.',
    'sf.inviteColleague': 'Invite a colleague',
    'sf.lastSignIn': 'Last signed in',
    'sf.noStaff': 'No staff yet',
    'sf.inviteHint': 'Invite colleagues to help run this account.',
    'sf.inviteSomeone': 'Invite someone',
    'sf.sendInvitation': 'Send invitation',
    'bl.subtitle': 'Your agreement with the platform, and what you owe.',
    'bl.noInvoices': 'No invoices yet',
    'bl.invoiceHint': 'The platform issues an invoice at the start of each month.',
    'bl.yourContract': 'Your contract',
    'bl.commissionPer': 'Commission per booking',
    'bl.monthlyFee': 'Monthly fee',
    'bl.inEffect': 'In effect since',
    'bl.noContract': 'No contract is currently in force. Contact platform support.',
    'bl.thisMonth': 'This month so far',
    'bl.confirmed': 'Bookings confirmed',
    'bl.gross': 'Gross takings',
    'bl.accrued': 'Commission accrued',
    'bl.estimated': 'Estimated total',
    'bl.refundNote': 'Refunded bookings reverse their commission in the month of the refund.',
    'br.subtitle': 'How your booking app looks to passengers, in the browser and once installed.',
    'br.appName': 'App name',
    'br.appNameHint': '2–20 characters. This is the name on the home screen once installed.',
    'br.notReadable': 'Body text is not readable',
    'br.worthLook': 'Worth a look',
    'br.themeMode': 'Theme mode',
    'br.preview': 'Customer app preview',
    'br.unpublished': 'Unpublished changes',
    'br.identity': 'Identity',
    'br.maskable': 'Maskable icon — Android may crop to this rounded shape.',
    'br.fixBefore': 'Fix these before publishing.',
    'br.canPublish': 'You can still publish — this is a readability note.',
    'br.typeTheme': 'Type and theme',
    'br.livePreview': 'Live preview',
    'br.customerFeatures': 'Customer app features',
    'br.customerFeaturesHint': 'Choose what passengers can do with a booking, and in what order those actions appear. Turning one off hides it everywhere in the passenger app.',
    'br.openApp': 'Open app',
    'br.previewHint': 'Changes appear here immediately. Passengers see them once you publish.',
    'db.bookingsToday': 'Bookings today',
    'db.revenueToday': 'Revenue today',
    'db.netRefunds': 'Net of refunds',
    'db.revenueWeek': 'Revenue this week',
    'db.openDisputes': 'Open disputes',
    'db.todaysDepartures': 'Today’s departures',
    'db.nothingToday': 'Nothing scheduled today.',
    'db.trendTitle': 'Bookings by month',
    'db.trendHint': 'Confirmed bookings, {months} months back.',
    'db.trendSeries': 'Bookings per month',
    'db.revenueTrendTitle': 'Revenue by month',
    'db.revenueTrendHint': 'Net of refunds, in {currency}.',
    'db.revenueSeries': 'Revenue per month',
    'db.routesTitle': 'Routes by load factor',
    'db.routesHint': 'Seats sold against seats offered, on departures that have run.',
    'db.window12': '12 months',
    'db.window24': '24 months',
    'db.noActivity': 'No activity in this window',
    'db.seatsSold': '{count} seats sold',
    'db.bookingsCount': '{count} bookings',
    'db.disputesWaiting': 'Waiting for a reply',
    'db.disputesNone': 'Nothing outstanding',
    'db.awaitingPayment': '{count} bookings awaiting payment. Seats are released automatically when the payment window closes.',
    'db.loadFactor': '{percent}% full',
    'ai.yourName': 'Your name',
    'ai.min10': 'At least 10 characters.',
    'ai.setUp': 'Set up your account',
    'ai.setUpHint': 'Choose a password and you are in. Your role has already been assigned.',
    'ai.setAndSignIn': 'Set password and sign in',
    'brand.suffix': 'Operations',
    'nav.section.today': 'Today',
    'nav.section.network': 'Network',
    'nav.section.business': 'Business',
    'nav.section.admin': 'Administration',
    'nav.dashboard': 'Dashboard',
    'nav.routes': 'Routes',
    'nav.trips': 'Trips',
    'nav.bookings': 'Bookings',
    'nav.disputes': 'Disputes',
    'nav.network': 'Stops & routes',
    'nav.fleet': 'Fleet',
    'nav.timetables': 'Timetables',
    'nav.branding': 'Branding',
    'nav.promotions': 'Promotions',
    'nav.settings': 'Settings',
    'nav.staff': 'Staff',
    'nav.roles': 'Roles',
    'nav.billing': 'Billing',
    'shell.signOut': 'Sign out',
    'shell.language': 'Language',
    'shell.profile': 'Profile',
    'profile.title': 'Profile',
    'profile.heading': 'Your profile',
    'profile.hint': 'Your name and contact details, and the picture other staff see beside your name.',
    'profile.photo': 'Profile picture',
    'profile.upload': 'Upload a picture',
    'profile.replace': 'Replace picture',
    'profile.removePhoto': 'Remove picture',
    'profile.photoHint': 'PNG, JPEG or WebP, up to 2 MB. Square works best.',
    'profile.name': 'Full name',
    'profile.phone': 'Phone',
    'profile.phoneHint': 'Optional. Colleagues use it to reach you about a trip.',
    'profile.email': 'Email',
    'profile.emailLocked': 'Your email is how you sign in, so it is changed by an administrator rather than here.',
    'profile.save': 'Save changes',
    'profile.saved': 'Profile updated.',
    'profile.saveFailed': 'Could not save that. Try again.',
    'profile.photoFailed': 'That picture could not be uploaded.',
    'shell.openMenu': 'Open menu',
    'shell.closeMenu': 'Close menu',
    'common.loading': 'Loading…',
    'common.retry': 'Try again',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.close': 'Close',
    'common.name': 'Name',
    'common.email': 'Email',
    'notif.title': 'Needs attention',
    'notif.open': 'Open notifications',
    'notif.empty': 'Nothing needs you right now.',
    'notif.emptyHint': 'Open disputes, unsold departures and today’s boarding show up here.',
    'notif.disputes': '{count} open dispute',
    'notif.disputes_other': '{count} open disputes',
    'notif.disputesHint': 'A passenger is waiting on a decision.',
    'notif.emptyTrips': '{count} departure with no bookings',
    'notif.emptyTrips_other': '{count} departures with no bookings',
    'notif.emptyTripsHint': 'Running today and still empty.',
    'dash.title': 'Dashboard',
    'dash.subtitle': 'How your service is running.',
    'dash.today': 'Today',
    'dash.departuresToday': 'Departures today',
    'dash.seatsSold': 'Seats sold',
    'dash.revenue': 'Revenue',
    'dash.revenueHint': 'After platform commission',
    'dash.load': 'Average load',
    'dash.loadHint': 'Across departures that have run',
    'routes.title': 'Route performance',
    'routes.subtitle': 'Which of your routes are working.',
    'routes.departed': 'Departed',
    'routes.selling': 'Selling now',
    'routes.windowHint.departed': 'Departures that have run, in the last {days} days. Load factor is final.',
    'routes.windowHint.selling': 'Departures still on sale, in the next {days} days. Load factor is how full they are so far.',
    'routes.colRoute': 'Route',
    'routes.colDepartures': 'Departures',
    'routes.colSeats': 'Seats',
    'routes.colLoad': 'Load',
    'routes.colGross': 'Gross',
    'routes.colNet': 'You keep',
    'routes.emptyTitle': 'No departures in this window',
    'routes.emptyBody': 'Once trips run, their load and revenue appear here.',
    'signIn.title': 'Sign in',
    'signIn.subtitle': 'Your account decides which bus company you manage.',
    'signIn.email': 'Email',
    'signIn.password': 'Password',
    'signIn.submit': 'Sign in',
  },

  fr: {
    'se.paxLanguage': 'Langue des passagers',
    'se.paxLangHint': 'La langue d’ouverture de votre site. Les passagers peuvent en changer. Laissez sur « Suivre leur appareil » pour utiliser la langue de leur téléphone.',
    'se.followDevice': 'Suivre leur appareil',
    'db.allTimesIn': '{date} · toutes les heures en {tz}',
    'tt.subtitle': 'Horaires récurrents. Les heures sont locales à {tz}.',
    'trips.subtitle': 'Départs le {date}, en {tz}.',
    'f.cancel': 'Annuler',
    'f.timezone': 'Fuseau horaire',
    'f.currency': 'Devise',
    'f.invoices': 'Factures',
    'brand.ops': 'BusBook Exploitation',
    'f.status': 'Statut',
    'f.date': 'Date',
    'f.route': 'Ligne',
    'f.bus': 'Bus',
    'f.note': 'Note',
    'f.departure': 'Départ',
    'f.departs': 'Départ',
    'f.booked': 'Réservé',
    'f.reference': 'Référence',
    'f.passenger': 'Passager',
    'f.passengers': 'Passagers',
    'f.trip': 'Trajet',
    'f.seats': 'Places',
    'f.total': 'Total',
    'f.paid': 'Payé',
    'f.value': 'Montant',
    'f.opened': 'Ouvert le',
    'f.booking': 'Réservation',
    'f.journey': 'Trajet',
    'f.payment': 'Paiement',
    'f.provider': 'Prestataire',
    'f.search': 'Rechercher',
    'f.clear': 'Effacer',
    'f.all': 'Tous',
    'f.reply': 'Répondre',
    'f.resolve': 'Clore',
    'f.close': 'Fermer',
    'f.reject': 'Rejeter',
    'f.callingAt': 'Dessert',
    'f.operations': 'Exploitation',
    'f.conversation': 'Échanges',
    'f.resolution': 'Résolution',
    'f.request': 'Demande',
    'st.scheduled': 'Programmé',
    'st.delayed': 'Retardé',
    'st.departed': 'Parti',
    'st.cancelled': 'Annulé',
    'st.confirmed': 'Confirmé',
    'st.awaiting': 'En attente de paiement',
    'st.refunded': 'Remboursé',
    'st.expired': 'Expiré',
    'st.open': 'Ouvert',
    'st.inReview': 'En examen',
    'st.closed': 'Clos',
    'trips.delayBy': 'Retard (minutes)',
    'trips.noteForPax': 'Note aux passagers',
    'trips.noteHint': 'Affichée sur leur billet, ex. « Départ porte 4 ».',
    'trips.oneOff': 'Départ exceptionnel',
    'trips.addOneOff': 'Ajouter un départ exceptionnel',
    'trips.departureTime': 'Heure de départ',
    'trips.noteOptional': 'Facultatif. Affiché aux passagers, ex. « Départ porte 4 ».',
    'trips.emptyTitle': 'Aucun départ à cette date',
    'trips.emptyBody': 'Changez la date, ou vérifiez qu’un horaire couvre ce jour.',
    'trips.manifest': 'Liste des passagers',
    'trips.manifestEmpty': 'Aucun passager confirmé.',
    'trips.applyDelay': 'Appliquer le retard',
    'trips.sendNote': 'Envoyer la note',
    'trips.cancelDep': 'Annuler ce départ',
    'trips.cancelHint': 'Les passagers sont prévenus et remboursés automatiquement.',
    'trips.create': 'Créer le départ',
    'trips.chooseRoute': 'Choisir une ligne',
    'trips.chooseBus': 'Choisir un bus',
    'bk.searchHint': 'Recherchez par référence, e-mail, nom ou passager.',
    'bk.searchPh': 'RF-8K3D2, nom ou e-mail',
    'bk.openRequest': 'Demande en cours',
    'bk.commission': 'Commission de la plateforme',
    'bk.refund': 'Rembourser et libérer les places',
    'bk.noRefundRole': 'Votre rôle ne permet pas les remboursements.',
    'dp.subtitle': 'Demandes de remboursement et réclamations des passagers.',
    'dp.replyTo': 'Répondre au passager',
    'dp.resolutionNote': 'Note de résolution',
    'dp.sentToPax': 'Envoyé au passager.',
    'dp.emptyBody': 'Les demandes des passagers sur leurs réservations apparaissent ici.',
    'dp.bookingStatus': 'Statut de la réservation',
    'dp.markInReview': 'Marquer en examen',
    'dp.approveRefund': 'Approuver le remboursement',
    'f.name': 'Nom',
    'f.city': 'Ville',
    'f.address': 'Adresse',
    'f.stop': 'Arrêt',
    'f.stops': 'Arrêts',
    'f.routes': 'Lignes',
    'f.duration': 'Durée',
    'f.timetables': 'Horaires',
    'f.fares': 'Tarifs',
    'f.save': 'Enregistrer',
    'f.from': 'Du',
    'f.until': 'Au',
    'f.role': 'Rôle',
    'f.email': 'E-mail',
    'f.password': 'Mot de passe',
    'f.type': 'Type',
    'f.period': 'Période',
    'f.invoice': 'Facture',
    'f.layout': 'Plan',
    'f.plate': 'Plaque',
    'f.rows': 'Rangées',
    'f.columns': 'Colonnes',
    'f.runs': 'Circule',
    'f.valid': 'Validité',
    'f.trips': 'Trajets',
    'f.buses': 'Bus',
    'f.show': 'Afficher',
    'f.none': 'Aucun',
    'f.publish': 'Publier',
    'f.font': 'Police',
    'f.menu': 'Menu',
    'f.text': 'Texte',
    'f.logo': 'Logo',
    'f.colours': 'Couleurs',
    'f.primary': 'Principale',
    'f.secondary': 'Secondaire',
    'f.background': 'Fond',
    'f.surface': 'Surface',
    'nw.subtitle': 'Où vos bus s’arrêtent, et dans quel ordre.',
    'nw.nameHint': 'Ce que verront les passagers, ex. « Gare centrale ».',
    'nw.addrHint': 'Facultatif. Aide les passagers à trouver le point de départ.',
    'nw.routeName': 'Nom de la ligne',
    'nw.routeHint': 'ex. « Lagos → Abuja express ».',
    'nw.noRoutes': 'Aucune ligne',
    'nw.noStops': 'Aucun arrêt',
    'nw.addStopsHint': 'Ajoutez les lieux où vos bus prennent et déposent.',
    'nw.addStop': 'Ajouter un arrêt',
    'nw.newRoute': 'Nouvelle ligne',
    'nw.saveStop': 'Enregistrer l’arrêt',
    'nw.showToPax': 'Afficher aux passagers',
    'nw.saveRoute': 'Enregistrer la ligne',
    'nw.stopsInOrder': 'Arrêts dans l’ordre',
    'nw.chooseStop': 'Choisir un arrêt',
    'nw.saveFares': 'Enregistrer les tarifs',
    'nw.fromTo': 'De \\ À',
    'fl.subtitle': 'Vos bus et les plans de sièges qu’ils utilisent.',
    'fl.nameHint': 'Le nom utilisé par votre personnel, ex. « Autocar 12 ».',
    'fl.plateNumber': 'Numéro d’immatriculation',
    'fl.seatLayout': 'Plan de sièges',
    'fl.grid': 'Grille',
    'fl.usedBy': 'Utilisé par',
    'fl.noBuses': 'Aucun bus',
    'fl.noLayouts': 'Aucun plan de sièges',
    'fl.layoutHint': 'Un plan décrit où se trouvent les sièges, les allées et les portes.',
    'fl.newLayout': 'Nouveau plan',
    'fl.addBus': 'Ajouter un bus',
    'fl.seatLayouts': 'Plans de sièges',
    'fl.saveBus': 'Enregistrer le bus',
    'fl.chooseLayout': 'Choisir un plan',
    'fl.inService': 'En service',
    'fl.saveLayout': 'Enregistrer le plan',
    'fl.seatTiers': 'Catégories de sièges',
    'fl.newTier': 'Nouvelle catégorie',
    'fl.tierName': 'Nom',
    'fl.markup': 'Majoration',
    'fl.tierSeatCount': 'Sièges',
    'fl.saveTier': 'Enregistrer la catégorie',
    'fl.deleteTier': 'Supprimer',
    'fl.defaultTierHint': 'La catégorie par défaut ne peut pas être supprimée.',
    'fl.tierInUseHint': 'Réaffectez d’abord ces sièges à une autre catégorie.',
    'tt.busClash': 'Ce bus est déjà programmé',
    'tt.blankHint': 'Laissez vide pour une durée illimitée.',
    'tt.noTimetables': 'Aucun horaire',
    'tt.newTimetable': 'Nouvel horaire',
    'tt.saveTimetable': 'Enregistrer l’horaire',
    'tt.savedAnyway': 'Enregistré malgré tout — vérifiez que le bus peut assurer les deux, ou changez de bus.',
    'tt.runsOn': 'Circule le',
    'tt.activeHint': 'Actif — générer les départs à partir de cet horaire',
    'se.subtitle': 'Le comportement de votre site de réservation pour les passagers.',
    'se.tzHint': 'Toutes les heures de départ saisies et affichées sont dans ce fuseau.',
    'se.cutoff': 'Délai d’annulation (heures)',
    'se.cutoffHint': 'Les passagers peuvent annuler eux-mêmes jusqu’à ce délai avant le départ.',
    'se.payWindow': 'Délai de paiement (minutes)',
    'se.payHint': 'Les réservations impayées expirent et les places sont libérées.',
    'se.reminder': 'Rappel avant départ (minutes)',
    'se.between': 'Entre 15 et 1440.',
    'se.maxSeats': 'Places max par réservation',
    'se.supportEmail': 'E-mail d’assistance',
    'se.supportHint': 'Affiché sur votre page d’aide, et utilisé comme adresse de réponse des e-mails.',
    'se.supportPhone': 'Téléphone d’assistance',
    'se.about': 'À propos de votre compagnie',
    'se.faq': 'FAQ',
    'se.saveSettings': 'Enregistrer les paramètres',
    'se.locale': 'Paramètres régionaux',
    'se.policy': 'Règles de réservation',
    'se.supportContact': 'Contact d’assistance',
    'se.helpContent': 'Contenu de la page d’aide',
    'sf.subtitle': 'Qui peut se connecter à ce compte, et ce qu’ils peuvent faire.',
    'sf.inviteColleague': 'Inviter un collègue',
    'sf.lastSignIn': 'Dernière connexion',
    'sf.noStaff': 'Aucun membre',
    'sf.inviteHint': 'Invitez des collègues pour vous aider à gérer ce compte.',
    'sf.inviteSomeone': 'Inviter quelqu’un',
    'sf.sendInvitation': 'Envoyer l’invitation',
    'bl.subtitle': 'Votre contrat avec la plateforme, et ce que vous devez.',
    'bl.noInvoices': 'Aucune facture',
    'bl.invoiceHint': 'La plateforme émet une facture au début de chaque mois.',
    'bl.yourContract': 'Votre contrat',
    'bl.commissionPer': 'Commission par réservation',
    'bl.monthlyFee': 'Forfait mensuel',
    'bl.inEffect': 'En vigueur depuis',
    'bl.noContract': 'Aucun contrat en vigueur. Contactez l’assistance de la plateforme.',
    'bl.thisMonth': 'Ce mois-ci',
    'bl.confirmed': 'Réservations confirmées',
    'bl.gross': 'Recettes brutes',
    'bl.accrued': 'Commission accumulée',
    'bl.estimated': 'Total estimé',
    'bl.refundNote': 'Les réservations remboursées annulent leur commission le mois du remboursement.',
    'br.subtitle': 'L’apparence de votre application pour les passagers, dans le navigateur et une fois installée.',
    'br.appName': 'Nom de l’application',
    'br.appNameHint': '2 à 20 caractères. C’est le nom sur l’écran d’accueil une fois installée.',
    'br.notReadable': 'Le texte n’est pas lisible',
    'br.worthLook': 'À vérifier',
    'br.themeMode': 'Mode du thème',
    'br.preview': 'Aperçu de l’application passagers',
    'br.unpublished': 'Modifications non publiées',
    'br.identity': 'Identité',
    'br.maskable': 'Icône masquable — Android peut la rogner à cette forme arrondie.',
    'br.fixBefore': 'Corrigez ceci avant de publier.',
    'br.canPublish': 'Vous pouvez publier — ceci est une remarque de lisibilité.',
    'br.typeTheme': 'Typographie et thème',
    'br.livePreview': 'Aperçu en direct',
    'br.customerFeatures': 'Fonctionnalités de l’app passager',
    'br.customerFeaturesHint': 'Choisissez ce que les passagers peuvent faire avec une réservation, et dans quel ordre ces actions apparaissent. En désactiver une la masque partout dans l’app passager.',
    'br.openApp': 'Ouvrir l’application',
    'br.previewHint': 'Les modifications apparaissent ici immédiatement. Les passagers les voient après publication.',
    'db.bookingsToday': 'Réservations aujourd’hui',
    'db.revenueToday': 'Recettes aujourd’hui',
    'db.netRefunds': 'Net des remboursements',
    'db.revenueWeek': 'Recettes cette semaine',
    'db.openDisputes': 'Litiges ouverts',
    'db.todaysDepartures': 'Départs du jour',
    'db.nothingToday': 'Rien de programmé aujourd’hui.',
    'db.trendTitle': 'Réservations par mois',
    'db.trendHint': 'Réservations confirmées, sur {months} mois.',
    'db.trendSeries': 'Réservations par mois',
    'db.revenueTrendTitle': 'Recettes par mois',
    'db.revenueTrendHint': 'Net des remboursements, en {currency}.',
    'db.revenueSeries': 'Recettes par mois',
    'db.routesTitle': 'Lignes par taux de remplissage',
    'db.routesHint': 'Places vendues sur places offertes, pour les départs effectués.',
    'db.window12': '12 mois',
    'db.window24': '24 mois',
    'db.noActivity': 'Aucune activité sur cette période',
    'db.seatsSold': '{count} places vendues',
    'db.bookingsCount': '{count} réservations',
    'db.disputesWaiting': 'En attente de réponse',
    'db.disputesNone': 'Rien en suspens',
    'db.awaitingPayment': '{count} réservations en attente de paiement. Les places sont libérées automatiquement à la clôture du délai.',
    'db.loadFactor': '{percent}% rempli',
    'ai.yourName': 'Votre nom',
    'ai.min10': 'Au moins 10 caractères.',
    'ai.setUp': 'Configurez votre compte',
    'ai.setUpHint': 'Choisissez un mot de passe et c’est fait. Votre rôle est déjà attribué.',
    'ai.setAndSignIn': 'Définir le mot de passe et se connecter',
    'brand.suffix': 'Exploitation',
    'nav.section.today': 'Aujourd’hui',
    'nav.section.network': 'Réseau',
    'nav.section.business': 'Activité',
    'nav.section.admin': 'Administration',
    'nav.dashboard': 'Tableau de bord',
    'nav.routes': 'Lignes',
    'nav.trips': 'Trajets',
    'nav.bookings': 'Réservations',
    'nav.disputes': 'Litiges',
    'nav.network': 'Arrêts et lignes',
    'nav.fleet': 'Flotte',
    'nav.timetables': 'Horaires',
    'nav.branding': 'Identité',
    'nav.promotions': 'Promotions',
    'nav.settings': 'Paramètres',
    'nav.staff': 'Équipe',
    'nav.roles': 'Rôles',
    'nav.billing': 'Facturation',
    'shell.signOut': 'Se déconnecter',
    'shell.language': 'Langue',
    'shell.profile': 'Profil',
    'profile.title': 'Profil',
    'profile.heading': 'Votre profil',
    'profile.hint': 'Vos nom et coordonnées, et la photo que vos collègues voient à côté de votre nom.',
    'profile.photo': 'Photo de profil',
    'profile.upload': 'Téléverser une photo',
    'profile.replace': 'Remplacer la photo',
    'profile.removePhoto': 'Retirer la photo',
    'profile.photoHint': 'PNG, JPEG ou WebP, jusqu’à 2 Mo. Le carré rend le mieux.',
    'profile.name': 'Nom complet',
    'profile.phone': 'Téléphone',
    'profile.phoneHint': 'Facultatif. Vos collègues l’utilisent pour vous joindre au sujet d’un trajet.',
    'profile.email': 'E-mail',
    'profile.emailLocked': 'Votre e-mail sert à vous connecter ; il est modifié par un administrateur, pas ici.',
    'profile.save': 'Enregistrer',
    'profile.saved': 'Profil mis à jour.',
    'profile.saveFailed': 'Enregistrement impossible. Réessayez.',
    'profile.photoFailed': 'Cette photo n’a pas pu être téléversée.',
    'shell.openMenu': 'Ouvrir le menu',
    'shell.closeMenu': 'Fermer le menu',
    'common.loading': 'Chargement…',
    'common.retry': 'Réessayer',
    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.close': 'Fermer',
    'common.name': 'Nom',
    'common.email': 'E-mail',
    'notif.title': 'À traiter',
    'notif.open': 'Ouvrir les notifications',
    'notif.empty': 'Rien ne vous attend pour l’instant.',
    'notif.emptyHint': 'Les litiges ouverts, les départs sans réservation et l’embarquement du jour apparaissent ici.',
    'notif.disputes': '{count} litige ouvert',
    'notif.disputes_other': '{count} litiges ouverts',
    'notif.disputesHint': 'Un passager attend une décision.',
    'notif.emptyTrips': '{count} départ sans réservation',
    'notif.emptyTrips_other': '{count} départs sans réservation',
    'notif.emptyTripsHint': 'Au départ aujourd’hui et toujours vide.',
    'dash.title': 'Tableau de bord',
    'dash.subtitle': 'Comment votre service tourne.',
    'dash.today': 'Aujourd’hui',
    'dash.departuresToday': 'Départs aujourd’hui',
    'dash.seatsSold': 'Places vendues',
    'dash.revenue': 'Recettes',
    'dash.revenueHint': 'Après commission de la plateforme',
    'dash.load': 'Remplissage moyen',
    'dash.loadHint': 'Sur les départs effectués',
    'routes.title': 'Performance des lignes',
    'routes.subtitle': 'Quelles lignes fonctionnent chez vous.',
    'routes.departed': 'Partis',
    'routes.selling': 'En vente',
    'routes.windowHint.departed': 'Départs effectués, sur les {days} derniers jours. Le remplissage est définitif.',
    'routes.windowHint.selling': 'Départs encore en vente, sur les {days} prochains jours. Le remplissage est celui atteint à ce jour.',
    'routes.colRoute': 'Ligne',
    'routes.colDepartures': 'Départs',
    'routes.colSeats': 'Places',
    'routes.colLoad': 'Remplissage',
    'routes.colGross': 'Brut',
    'routes.colNet': 'Vous gardez',
    'routes.emptyTitle': 'Aucun départ sur cette période',
    'routes.emptyBody': 'Dès que des trajets sont effectués, leur remplissage et leurs recettes apparaissent ici.',
    'signIn.title': 'Connexion',
    'signIn.subtitle': 'Votre compte détermine la compagnie que vous gérez.',
    'signIn.email': 'E-mail',
    'signIn.password': 'Mot de passe',
    'signIn.submit': 'Se connecter',
  },

  sw: {
    'se.paxLanguage': 'Lugha ya abiria',
    'se.paxLangHint': 'Lugha ambayo tovuti yako inafunguka nayo. Abiria wanaweza kuibadilisha wenyewe. Acha “Fuata kifaa chao” ili kutumia lugha ya simu yao.',
    'se.followDevice': 'Fuata kifaa chao',
    'db.allTimesIn': '{date} · nyakati zote katika {tz}',
    'tt.subtitle': 'Ratiba za kujirudia. Nyakati ni za {tz}.',
    'trips.subtitle': 'Safari za {date}, katika {tz}.',
    'f.cancel': 'Ghairi',
    'f.timezone': 'Saa za eneo',
    'f.currency': 'Sarafu',
    'f.invoices': 'Ankara',
    'brand.ops': 'BusBook Uendeshaji',
    'f.status': 'Hali',
    'f.date': 'Tarehe',
    'f.route': 'Njia',
    'f.bus': 'Basi',
    'f.note': 'Dokezo',
    'f.departure': 'Kuondoka',
    'f.departs': 'Inaondoka',
    'f.booked': 'Zilizowekwa',
    'f.reference': 'Kumbukumbu',
    'f.passenger': 'Abiria',
    'f.passengers': 'Abiria',
    'f.trip': 'Safari',
    'f.seats': 'Viti',
    'f.total': 'Jumla',
    'f.paid': 'Imelipwa',
    'f.value': 'Thamani',
    'f.opened': 'Ilifunguliwa',
    'f.booking': 'Nafasi',
    'f.journey': 'Safari',
    'f.payment': 'Malipo',
    'f.provider': 'Mtoa huduma',
    'f.search': 'Tafuta',
    'f.clear': 'Futa',
    'f.all': 'Zote',
    'f.reply': 'Jibu',
    'f.resolve': 'Tatua',
    'f.close': 'Funga',
    'f.reject': 'Kataa',
    'f.callingAt': 'Inapita',
    'f.operations': 'Uendeshaji',
    'f.conversation': 'Mazungumzo',
    'f.resolution': 'Suluhisho',
    'f.request': 'Ombi',
    'st.scheduled': 'Imepangwa',
    'st.delayed': 'Imechelewa',
    'st.departed': 'Imeondoka',
    'st.cancelled': 'Imefutwa',
    'st.confirmed': 'Imethibitishwa',
    'st.awaiting': 'Inasubiri malipo',
    'st.refunded': 'Imerejeshwa',
    'st.expired': 'Imeisha',
    'st.open': 'Wazi',
    'st.inReview': 'Inachunguzwa',
    'st.closed': 'Imefungwa',
    'trips.delayBy': 'Chelewesha (dakika)',
    'trips.noteForPax': 'Dokezo kwa abiria',
    'trips.noteHint': 'Inaonyeshwa kwenye tikiti yao, mf. “Inaondoka Lango 4”.',
    'trips.oneOff': 'Safari ya mara moja',
    'trips.addOneOff': 'Ongeza safari ya mara moja',
    'trips.departureTime': 'Muda wa kuondoka',
    'trips.noteOptional': 'Si lazima. Inaonyeshwa kwa abiria, mf. “Inaondoka Lango 4”.',
    'trips.emptyTitle': 'Hakuna safari tarehe hii',
    'trips.emptyBody': 'Badilisha tarehe, au hakikisha ratiba inashughulikia siku hii.',
    'trips.manifest': 'Orodha ya abiria',
    'trips.manifestEmpty': 'Bado hakuna abiria aliyethibitishwa.',
    'trips.applyDelay': 'Weka ucheleweshaji',
    'trips.sendNote': 'Tuma dokezo',
    'trips.cancelDep': 'Futa safari hii',
    'trips.cancelHint': 'Abiria wanaarifiwa na kurejeshewa fedha kiotomatiki.',
    'trips.create': 'Unda safari',
    'trips.chooseRoute': 'Chagua njia',
    'trips.chooseBus': 'Chagua basi',
    'bk.searchHint': 'Tafuta kwa kumbukumbu, barua pepe, jina au abiria.',
    'bk.searchPh': 'RF-8K3D2, jina au barua pepe',
    'bk.openRequest': 'Ombi wazi',
    'bk.commission': 'Kamisheni ya jukwaa',
    'bk.refund': 'Rejesha fedha na uachie viti',
    'bk.noRefundRole': 'Wadhifa wako hauwezi kutoa marejesho.',
    'dp.subtitle': 'Maombi ya kurejeshewa fedha na malalamiko ya abiria.',
    'dp.replyTo': 'Mjibu abiria',
    'dp.resolutionNote': 'Dokezo la suluhisho',
    'dp.sentToPax': 'Imetumwa kwa abiria.',
    'dp.emptyBody': 'Maombi ya abiria kuhusu nafasi zao huonekana hapa.',
    'dp.bookingStatus': 'Hali ya nafasi',
    'dp.markInReview': 'Weka inachunguzwa',
    'dp.approveRefund': 'Idhinisha marejesho',
    'f.name': 'Jina',
    'f.city': 'Jiji',
    'f.address': 'Anwani',
    'f.stop': 'Kituo',
    'f.stops': 'Vituo',
    'f.routes': 'Njia',
    'f.duration': 'Muda',
    'f.timetables': 'Ratiba',
    'f.fares': 'Nauli',
    'f.save': 'Hifadhi',
    'f.from': 'Kuanzia',
    'f.until': 'Hadi',
    'f.role': 'Wadhifa',
    'f.email': 'Barua pepe',
    'f.password': 'Nenosiri',
    'f.type': 'Aina',
    'f.period': 'Kipindi',
    'f.invoice': 'Ankara',
    'f.layout': 'Mpangilio',
    'f.plate': 'Namba ya gari',
    'f.rows': 'Safu',
    'f.columns': 'Nguzo',
    'f.runs': 'Inakwenda',
    'f.valid': 'Halali',
    'f.trips': 'Safari',
    'f.buses': 'Mabasi',
    'f.show': 'Onyesha',
    'f.none': 'Hakuna',
    'f.publish': 'Chapisha',
    'f.font': 'Fonti',
    'f.menu': 'Menyu',
    'f.text': 'Maandishi',
    'f.logo': 'Nembo',
    'f.colours': 'Rangi',
    'f.primary': 'Kuu',
    'f.secondary': 'Ya pili',
    'f.background': 'Mandharinyuma',
    'f.surface': 'Uso',
    'nw.subtitle': 'Mabasi yako yanasimama wapi, na kwa mpangilio gani.',
    'nw.nameHint': 'Kile abiria wataona, mf. “Stesheni Kuu”.',
    'nw.addrHint': 'Si lazima. Husaidia abiria kupata mahali pa kupanda.',
    'nw.routeName': 'Jina la njia',
    'nw.routeHint': 'mf. “Lagos → Abuja express”.',
    'nw.noRoutes': 'Hakuna njia bado',
    'nw.noStops': 'Hakuna vituo bado',
    'nw.addStopsHint': 'Ongeza sehemu ambazo mabasi yako yanachukua na kushusha abiria.',
    'nw.addStop': 'Ongeza kituo',
    'nw.newRoute': 'Njia mpya',
    'nw.saveStop': 'Hifadhi kituo',
    'nw.showToPax': 'Onyesha kwa abiria',
    'nw.saveRoute': 'Hifadhi njia',
    'nw.stopsInOrder': 'Vituo kwa mpangilio',
    'nw.chooseStop': 'Chagua kituo',
    'nw.saveFares': 'Hifadhi nauli',
    'nw.fromTo': 'Kutoka \\ Hadi',
    'fl.subtitle': 'Mabasi yako na mipangilio ya viti wanayotumia.',
    'fl.nameHint': 'Jina wafanyakazi wako wanalotumia, mf. “Coach 12”.',
    'fl.plateNumber': 'Namba ya usajili',
    'fl.seatLayout': 'Mpangilio wa viti',
    'fl.grid': 'Gridi',
    'fl.usedBy': 'Inatumiwa na',
    'fl.noBuses': 'Hakuna mabasi bado',
    'fl.noLayouts': 'Hakuna mipangilio ya viti bado',
    'fl.layoutHint': 'Mpangilio unaeleza viti, njia na milango vilipo kwenye basi.',
    'fl.newLayout': 'Mpangilio mpya',
    'fl.addBus': 'Ongeza basi',
    'fl.seatLayouts': 'Mipangilio ya viti',
    'fl.saveBus': 'Hifadhi basi',
    'fl.chooseLayout': 'Chagua mpangilio',
    'fl.inService': 'Inatumika',
    'fl.saveLayout': 'Hifadhi mpangilio',
    'fl.seatTiers': 'Viwango vya viti',
    'fl.newTier': 'Kiwango kipya',
    'fl.tierName': 'Jina',
    'fl.markup': 'Nyongeza',
    'fl.tierSeatCount': 'Viti',
    'fl.saveTier': 'Hifadhi kiwango',
    'fl.deleteTier': 'Futa',
    'fl.defaultTierHint': 'Kiwango chaguo-msingi hakiwezi kufutwa.',
    'fl.tierInUseHint': 'Badilisha kwanza viti hivi kwenda kiwango kingine.',
    'tt.busClash': 'Basi hili tayari limepangwa',
    'tt.blankHint': 'Acha wazi ili iendelee bila kikomo.',
    'tt.noTimetables': 'Hakuna ratiba bado',
    'tt.newTimetable': 'Ratiba mpya',
    'tt.saveTimetable': 'Hifadhi ratiba',
    'tt.savedAnyway': 'Imehifadhiwa hata hivyo — hakikisha basi linaweza safari zote mbili, au badilisha basi.',
    'tt.runsOn': 'Inakwenda',
    'tt.activeHint': 'Inatumika — tengeneza safari kutoka ratiba hii',
    'se.subtitle': 'Jinsi tovuti yako ya kuweka nafasi inavyofanya kazi kwa abiria.',
    'se.tzHint': 'Nyakati zote za kuondoka unazoweka na abiria wanaziona ziko katika saa hizi.',
    'se.cutoff': 'Muda wa mwisho wa kughairi (saa)',
    'se.cutoffHint': 'Abiria wanaweza kughairi wenyewe hadi muda huu kabla ya kuondoka.',
    'se.payWindow': 'Muda wa malipo (dakika)',
    'se.payHint': 'Nafasi zisizolipiwa huisha na viti huachiwa.',
    'se.reminder': 'Kikumbusho kabla (dakika)',
    'se.between': 'Kati ya 15 na 1440.',
    'se.maxSeats': 'Viti vya juu kwa kila nafasi',
    'se.supportEmail': 'Barua pepe ya msaada',
    'se.supportHint': 'Inaonyeshwa kwenye ukurasa wako wa Msaada, na hutumika kama anwani ya kujibu.',
    'se.supportPhone': 'Simu ya msaada',
    'se.about': 'Kuhusu kampuni yako',
    'se.faq': 'Maswali',
    'se.saveSettings': 'Hifadhi mipangilio',
    'se.locale': 'Mipangilio ya eneo',
    'se.policy': 'Sera ya kuweka nafasi',
    'se.supportContact': 'Mawasiliano ya msaada',
    'se.helpContent': 'Maudhui ya ukurasa wa msaada',
    'sf.subtitle': 'Nani anaweza kuingia katika akaunti hii, na anaweza kufanya nini.',
    'sf.inviteColleague': 'Mwalike mwenzako',
    'sf.lastSignIn': 'Aliingia mwisho',
    'sf.noStaff': 'Hakuna wafanyakazi bado',
    'sf.inviteHint': 'Waalike wenzako wakusaidie kuendesha akaunti hii.',
    'sf.inviteSomeone': 'Mwalike mtu',
    'sf.sendInvitation': 'Tuma mwaliko',
    'bl.subtitle': 'Makubaliano yako na jukwaa, na unachodaiwa.',
    'bl.noInvoices': 'Hakuna ankara bado',
    'bl.invoiceHint': 'Jukwaa hutoa ankara mwanzoni mwa kila mwezi.',
    'bl.yourContract': 'Mkataba wako',
    'bl.commissionPer': 'Kamisheni kwa kila nafasi',
    'bl.monthlyFee': 'Ada ya mwezi',
    'bl.inEffect': 'Inatumika tangu',
    'bl.noContract': 'Hakuna mkataba unaotumika sasa. Wasiliana na msaada wa jukwaa.',
    'bl.thisMonth': 'Mwezi huu hadi sasa',
    'bl.confirmed': 'Nafasi zilizothibitishwa',
    'bl.gross': 'Mapato ghafi',
    'bl.accrued': 'Kamisheni iliyokusanywa',
    'bl.estimated': 'Jumla inayokadiriwa',
    'bl.refundNote': 'Nafasi zilizorejeshwa hufuta kamisheni yake katika mwezi wa marejesho.',
    'br.subtitle': 'Jinsi programu yako inavyoonekana kwa abiria, kwenye kivinjari na ikiwa imesakinishwa.',
    'br.appName': 'Jina la programu',
    'br.appNameHint': 'Herufi 2–20. Hili ndilo jina kwenye skrini ya nyumbani ikiwa imesakinishwa.',
    'br.notReadable': 'Maandishi hayasomeki',
    'br.worthLook': 'Inafaa kuangalia',
    'br.themeMode': 'Hali ya mandhari',
    'br.preview': 'Onyesho la programu ya abiria',
    'br.unpublished': 'Mabadiliko ambayo hayajachapishwa',
    'br.identity': 'Utambulisho',
    'br.maskable': 'Aikoni inayoweza kukatwa — Android inaweza kuikata kwa umbo hili la duara.',
    'br.fixBefore': 'Rekebisha haya kabla ya kuchapisha.',
    'br.canPublish': 'Bado unaweza kuchapisha — hii ni dokezo la usomekaji.',
    'br.typeTheme': 'Fonti na mandhari',
    'br.livePreview': 'Onyesho la moja kwa moja',
    'br.customerFeatures': 'Vipengele vya programu ya abiria',
    'br.customerFeaturesHint': 'Chagua abiria wanaweza kufanya nini na nafasi yao, na mpangilio wa vitendo hivyo. Ukizima kimoja, hakitaonekana popote kwenye programu ya abiria.',
    'br.openApp': 'Fungua programu',
    'br.previewHint': 'Mabadiliko yanaonekana hapa mara moja. Abiria wanayaona ukishachapisha.',
    'db.bookingsToday': 'Nafasi leo',
    'db.revenueToday': 'Mapato leo',
    'db.netRefunds': 'Baada ya marejesho',
    'db.revenueWeek': 'Mapato wiki hii',
    'db.openDisputes': 'Migogoro iliyo wazi',
    'db.todaysDepartures': 'Safari za leo',
    'db.nothingToday': 'Hakuna kilichopangwa leo.',
    'db.trendTitle': 'Nafasi kwa mwezi',
    'db.trendHint': 'Nafasi zilizothibitishwa, miezi {months} iliyopita.',
    'db.trendSeries': 'Nafasi kwa mwezi',
    'db.revenueTrendTitle': 'Mapato kwa mwezi',
    'db.revenueTrendHint': 'Baada ya marejesho, kwa {currency}.',
    'db.revenueSeries': 'Mapato kwa mwezi',
    'db.routesTitle': 'Njia kwa kiwango cha ujazo',
    'db.routesHint': 'Viti vilivyouzwa dhidi ya vilivyotolewa, kwa safari zilizoondoka.',
    'db.window12': 'Miezi 12',
    'db.window24': 'Miezi 24',
    'db.noActivity': 'Hakuna shughuli katika kipindi hiki',
    'db.seatsSold': 'Viti {count} vimeuzwa',
    'db.bookingsCount': 'Nafasi {count}',
    'db.disputesWaiting': 'Inasubiri jibu',
    'db.disputesNone': 'Hakuna lililosalia',
    'db.awaitingPayment': 'Nafasi {count} zinasubiri malipo. Viti hurudishwa kiotomatiki muda wa malipo ukiisha.',
    'db.loadFactor': 'Imejaa {percent}%',
    'ai.yourName': 'Jina lako',
    'ai.min10': 'Angalau herufi 10.',
    'ai.setUp': 'Sanidi akaunti yako',
    'ai.setUpHint': 'Chagua nenosiri na umeingia. Wadhifa wako tayari umepangwa.',
    'ai.setAndSignIn': 'Weka nenosiri na uingie',
    'brand.suffix': 'Uendeshaji',
    'nav.section.today': 'Leo',
    'nav.section.network': 'Mtandao',
    'nav.section.business': 'Biashara',
    'nav.section.admin': 'Usimamizi',
    'nav.dashboard': 'Dashibodi',
    'nav.routes': 'Njia',
    'nav.trips': 'Safari',
    'nav.bookings': 'Nafasi',
    'nav.disputes': 'Migogoro',
    'nav.network': 'Vituo na njia',
    'nav.fleet': 'Magari',
    'nav.timetables': 'Ratiba',
    'nav.branding': 'Utambulisho',
    'nav.promotions': 'Promotions',
    'nav.settings': 'Mipangilio',
    'nav.staff': 'Wafanyakazi',
    'nav.roles': 'Nyadhifa',
    'nav.billing': 'Ankara',
    'shell.signOut': 'Toka',
    'shell.language': 'Lugha',
    'shell.profile': 'Wasifu',
    'profile.title': 'Wasifu',
    'profile.heading': 'Wasifu wako',
    'profile.hint': 'Jina na mawasiliano yako, na picha wafanyakazi wenzako wanayoiona kando ya jina lako.',
    'profile.photo': 'Picha ya wasifu',
    'profile.upload': 'Pakia picha',
    'profile.replace': 'Badilisha picha',
    'profile.removePhoto': 'Ondoa picha',
    'profile.photoHint': 'PNG, JPEG au WebP, hadi MB 2. Mraba hufaa zaidi.',
    'profile.name': 'Jina kamili',
    'profile.phone': 'Simu',
    'profile.phoneHint': 'Si lazima. Wenzako huitumia kukufikia kuhusu safari.',
    'profile.email': 'Barua pepe',
    'profile.emailLocked': 'Barua pepe yako ndiyo unayotumia kuingia, hivyo hubadilishwa na msimamizi, si hapa.',
    'profile.save': 'Hifadhi mabadiliko',
    'profile.saved': 'Wasifu umesasishwa.',
    'profile.saveFailed': 'Imeshindwa kuhifadhi. Jaribu tena.',
    'profile.photoFailed': 'Picha hiyo haikuweza kupakiwa.',
    'shell.openMenu': 'Fungua menyu',
    'shell.closeMenu': 'Funga menyu',
    'common.loading': 'Inapakia…',
    'common.retry': 'Jaribu tena',
    'common.cancel': 'Ghairi',
    'common.save': 'Hifadhi',
    'common.close': 'Funga',
    'common.name': 'Jina',
    'common.email': 'Barua pepe',
    'notif.title': 'Yanahitaji uangalizi',
    'notif.open': 'Fungua arifa',
    'notif.empty': 'Hakuna linalokusubiri kwa sasa.',
    'notif.emptyHint': 'Migogoro iliyo wazi, safari zisizo na nafasi zilizouzwa na upandaji wa leo huonekana hapa.',
    'notif.disputes': 'Mgogoro {count} uko wazi',
    'notif.disputes_other': 'Migogoro {count} iko wazi',
    'notif.disputesHint': 'Abiria anasubiri uamuzi.',
    'notif.emptyTrips': 'Safari {count} haina nafasi zilizouzwa',
    'notif.emptyTrips_other': 'Safari {count} hazina nafasi zilizouzwa',
    'notif.emptyTripsHint': 'Inaondoka leo na bado tupu.',
    'dash.title': 'Dashibodi',
    'dash.subtitle': 'Jinsi huduma yako inavyokwenda.',
    'dash.today': 'Leo',
    'dash.departuresToday': 'Safari za leo',
    'dash.seatsSold': 'Nafasi zilizouzwa',
    'dash.revenue': 'Mapato',
    'dash.revenueHint': 'Baada ya kamisheni ya jukwaa',
    'dash.load': 'Wastani wa kujaa',
    'dash.loadHint': 'Kwa safari zilizofanyika',
    'routes.title': 'Utendaji wa njia',
    'routes.subtitle': 'Njia zako zipi zinafanya kazi.',
    'routes.departed': 'Zilizoondoka',
    'routes.selling': 'Zinauzwa',
    'routes.windowHint.departed': 'Safari zilizofanyika, katika siku {days} zilizopita. Kujaa ni kwa mwisho.',
    'routes.windowHint.selling': 'Safari bado zinauzwa, katika siku {days} zijazo. Kujaa ni kiasi zilivyojaa hadi sasa.',
    'routes.colRoute': 'Njia',
    'routes.colDepartures': 'Safari',
    'routes.colSeats': 'Viti',
    'routes.colLoad': 'Kujaa',
    'routes.colGross': 'Jumla',
    'routes.colNet': 'Unabaki nayo',
    'routes.emptyTitle': 'Hakuna safari katika kipindi hiki',
    'routes.emptyBody': 'Safari zikishafanyika, kujaa na mapato yake huonekana hapa.',
    'signIn.title': 'Ingia',
    'signIn.subtitle': 'Akaunti yako inaamua kampuni unayosimamia.',
    'signIn.email': 'Barua pepe',
    'signIn.password': 'Nenosiri',
    'signIn.submit': 'Ingia',
  },

  ln: {
    'se.paxLanguage': 'Monɔkɔ ya bapaya',
    'se.paxLangHint': 'Monɔkɔ oyo site na yo efungwamaka na yango. Bapaya bakoki kobongola yango. Tiká na « Landa telefone na bango » mpo na kosalela monɔkɔ ya telefone na bango.',
    'se.followDevice': 'Landa telefone na bango',
    'db.allTimesIn': '{date} · bangonga nyonso na {tz}',
    'tt.subtitle': 'Bangonga oyo ezongelaka. Bangonga ezali ya {tz}.',
    'trips.subtitle': 'Mibembo ya {date}, na {tz}.',
    'f.cancel': 'Kotika',
    'f.timezone': 'Ngonga ya esika',
    'f.currency': 'Mbongo',
    'f.invoices': 'Bafaktire',
    'brand.ops': 'BusBook Mosala',
    'f.status': 'Ezalela',
    'f.date': 'Mokolo',
    'f.route': 'Nzela',
    'f.bus': 'Bisi',
    'f.note': 'Liyebisi',
    'f.departure': 'Kokende',
    'f.departs': 'Ekokende',
    'f.booked': 'Ekomami',
    'f.reference': 'Nimero',
    'f.passenger': 'Mopaya',
    'f.passengers': 'Bapaya',
    'f.trip': 'Mobembo',
    'f.seats': 'Bakiti',
    'f.total': 'Motuya nyonso',
    'f.paid': 'Efutami',
    'f.value': 'Motuya',
    'f.opened': 'Efungwamaki',
    'f.booking': 'Bokomisi',
    'f.journey': 'Mobembo',
    'f.payment': 'Lifuti',
    'f.provider': 'Mosali',
    'f.search': 'Koluka',
    'f.clear': 'Kolongola',
    'f.all': 'Nyonso',
    'f.reply': 'Koyanola',
    'f.resolve': 'Kosilisa',
    'f.close': 'Kokanga',
    'f.reject': 'Koboya',
    'f.callingAt': 'Ekoleka na',
    'f.operations': 'Mosala',
    'f.conversation': 'Masolo',
    'f.resolution': 'Ekateli',
    'f.request': 'Bosengi',
    'st.scheduled': 'Ebongisami',
    'st.delayed': 'Ezali na retard',
    'st.departed': 'Ekendeki',
    'st.cancelled': 'Elongolami',
    'st.confirmed': 'Endimami',
    'st.awaiting': 'Ezali kozela lifuti',
    'st.refunded': 'Ezongisami',
    'st.expired': 'Esili ntango',
    'st.open': 'Efungwami',
    'st.inReview': 'Ezali kotalema',
    'st.closed': 'Ekangami',
    'trips.delayBy': 'Retard (miniti)',
    'trips.noteForPax': 'Liyebisi mpo na bapaya',
    'trips.noteHint': 'Ekomonana na tike na bango, ndakisa « Ekokende na Porte 4 ».',
    'trips.oneOff': 'Mobembo ya mbala moko',
    'trips.addOneOff': 'Kobakisa mobembo ya mbala moko',
    'trips.departureTime': 'Ngonga ya kokende',
    'trips.noteOptional': 'Ezali na ntina makasi te. Ekomonana na bapaya, ndakisa « Ekokende na Porte 4 ».',
    'trips.emptyTitle': 'Mobembo te na mokolo oyo',
    'trips.emptyBody': 'Bongola mokolo, to talá soki ngonga ekokamba mokolo oyo.',
    'trips.manifest': 'Liste ya bapaya',
    'trips.manifestEmpty': 'Mopaya moko andimami naino te.',
    'trips.applyDelay': 'Kotia retard',
    'trips.sendNote': 'Kotinda liyebisi',
    'trips.cancelDep': 'Kolongola mobembo oyo',
    'trips.cancelHint': 'Bapaya bayebisami mpe bazongiseli bango mbongo na ndenge ya moto moko te.',
    'trips.create': 'Kosala mobembo',
    'trips.chooseRoute': 'Poná nzela',
    'trips.chooseBus': 'Poná bisi',
    'bk.searchHint': 'Luká na nimero, imeli, nkombo to mopaya.',
    'bk.searchPh': 'RF-8K3D2, nkombo to imeli',
    'bk.openRequest': 'Bosengi efungwami',
    'bk.commission': 'Komisiɔ ya plateforme',
    'bk.refund': 'Kozongisa mbongo mpe kotika bakiti',
    'bk.noRefundRole': 'Mokumba na yo ekoki kozongisa mbongo te.',
    'dp.subtitle': 'Bosengi ya kozongisa mbongo mpe kolela ya bapaya.',
    'dp.replyTo': 'Koyanola mopaya',
    'dp.resolutionNote': 'Liyebisi ya ekateli',
    'dp.sentToPax': 'Etindami epai ya mopaya.',
    'dp.emptyBody': 'Bosengi ya bapaya na tina ya bokomisi na bango ekomonana awa.',
    'dp.bookingStatus': 'Ezalela ya bokomisi',
    'dp.markInReview': 'Kotia na botali',
    'dp.approveRefund': 'Kondima kozongisa mbongo',
    'f.name': 'Nkombo',
    'f.city': 'Engumba',
    'f.address': 'Adrɛsi',
    'f.stop': 'Esika',
    'f.stops': 'Bisika',
    'f.routes': 'Banzela',
    'f.duration': 'Ntango',
    'f.timetables': 'Bangonga',
    'f.fares': 'Ntalo',
    'f.save': 'Kobomba',
    'f.from': 'Banda',
    'f.until': 'Tii',
    'f.role': 'Mokumba',
    'f.email': 'Imeli',
    'f.password': 'Motindo ya kokota',
    'f.type': 'Lolenge',
    'f.period': 'Eleko',
    'f.invoice': 'Faktire',
    'f.layout': 'Bobongisi',
    'f.plate': 'Plake',
    'f.rows': 'Milɔngɔ',
    'f.columns': 'Bakolonɛ',
    'f.runs': 'Ekendeke',
    'f.valid': 'Ekoki',
    'f.trips': 'Mibembo',
    'f.buses': 'Bibisi',
    'f.show': 'Kolakisa',
    'f.none': 'Eloko te',
    'f.publish': 'Kobimisa',
    'f.font': 'Fɔntɛ',
    'f.menu': 'Menu',
    'f.text': 'Makomi',
    'f.logo': 'Elembo',
    'f.colours': 'Balangi',
    'f.primary': 'Ya liboso',
    'f.secondary': 'Ya mibale',
    'f.background': 'Sima',
    'f.surface': 'Likolo',
    'nw.subtitle': 'Bibisi na yo etelemaka wapi, mpe na molɔngɔ nini.',
    'nw.nameHint': 'Oyo bapaya bakomona, ndakisa « Gare Centrale ».',
    'nw.addrHint': 'Ezali na ntina makasi te. Esalisaka bapaya kozwa esika ya komata.',
    'nw.routeName': 'Nkombo ya nzela',
    'nw.routeHint': 'ndakisa « Lagos → Abuja express ».',
    'nw.noRoutes': 'Nzela ezali naino te',
    'nw.noStops': 'Bisika ezali naino te',
    'nw.addStopsHint': 'Bakisá bisika oyo bibisi na yo ezwaka mpe ekitisaka bato.',
    'nw.addStop': 'Kobakisa esika',
    'nw.newRoute': 'Nzela ya sika',
    'nw.saveStop': 'Kobomba esika',
    'nw.showToPax': 'Kolakisa na bapaya',
    'nw.saveRoute': 'Kobomba nzela',
    'nw.stopsInOrder': 'Bisika na molɔngɔ',
    'nw.chooseStop': 'Poná esika',
    'nw.saveFares': 'Kobomba ntalo',
    'nw.fromTo': 'Banda \\ Tii',
    'fl.subtitle': 'Bibisi na yo mpe bobongisi ya bakiti oyo esalelaka.',
    'fl.nameHint': 'Nkombo oyo basali na yo babengaka yango, ndakisa « Coach 12 ».',
    'fl.plateNumber': 'Nimero ya plake',
    'fl.seatLayout': 'Bobongisi ya bakiti',
    'fl.grid': 'Grille',
    'fl.usedBy': 'Esalelami na',
    'fl.noBuses': 'Bibisi ezali naino te',
    'fl.noLayouts': 'Bobongisi ya bakiti ezali naino te',
    'fl.layoutHint': 'Bobongisi elakisi esika bakiti, banzela mpe baporte ezali na bisi.',
    'fl.newLayout': 'Bobongisi ya sika',
    'fl.addBus': 'Kobakisa bisi',
    'fl.seatLayouts': 'Bobongisi ya bakiti',
    'fl.saveBus': 'Kobomba bisi',
    'fl.chooseLayout': 'Poná bobongisi',
    'fl.inService': 'Ezali kosala',
    'fl.saveLayout': 'Kobomba bobongisi',
    'fl.seatTiers': 'Baklasi ya bakiti',
    'fl.newTier': 'Klasi ya sika',
    'fl.tierName': 'Nkombo',
    'fl.markup': 'Bobakisi',
    'fl.tierSeatCount': 'Bakiti',
    'fl.saveTier': 'Kobomba klasi',
    'fl.deleteTier': 'Kolongola',
    'fl.defaultTierHint': 'Klasi ya ebandeli ekoki kolongolama te.',
    'fl.tierInUseHint': 'Bongolá klasi ya bakiti oyo liboso.',
    'tt.busClash': 'Bisi oyo ebongisami deja',
    'tt.blankHint': 'Tiká mpamba mpo ekende ntango nyonso.',
    'tt.noTimetables': 'Bangonga ezali naino te',
    'tt.newTimetable': 'Ngonga ya sika',
    'tt.saveTimetable': 'Kobomba ngonga',
    'tt.savedAnyway': 'Ebombami atako bongo — talá soki bisi ekoki kosala mibembo nyonso mibale, to bongola bisi.',
    'tt.runsOn': 'Ekendeke na',
    'tt.activeHint': 'Ezali kosala — kobimisa mibembo uta na ngonga oyo',
    'se.subtitle': 'Ndenge site na yo ya bokomisi esalaka mpo na bapaya.',
    'se.tzHint': 'Bangonga nyonso ya kokende oyo okotisi mpe bapaya bamonaka ezali na ngonga oyo.',
    'se.cutoff': 'Ntango ya nsuka ya kolongola (bangonga)',
    'se.cutoffHint': 'Bapaya bakoki komilongola tii ntango oyo liboso ya kokende.',
    'se.payWindow': 'Ntango ya lifuti (miniti)',
    'se.payHint': 'Bokomisi oyo efutami te esilaka mpe bakiti etikami.',
    'se.reminder': 'Bokundoli liboso (miniti)',
    'se.between': 'Kati ya 15 mpe 1440.',
    'se.maxSeats': 'Bakiti mingi na bokomisi moko',
    'se.supportEmail': 'Imeli ya lisungi',
    'se.supportHint': 'Ekomonana na lokasa na yo ya Lisungi, mpe esalelami lokola adrɛsi ya koyanola.',
    'se.supportPhone': 'Telefone ya lisungi',
    'se.about': 'Na tina ya kompani na yo',
    'se.faq': 'Mituna',
    'se.saveSettings': 'Kobomba ebongiseli',
    'se.locale': 'Esika mpe monɔkɔ',
    'se.policy': 'Mibeko ya bokomisi',
    'se.supportContact': 'Kokutana mpo na lisungi',
    'se.helpContent': 'Makomi ya lokasa ya lisungi',
    'sf.subtitle': 'Nani akoki kokota na kɔntɛ oyo, mpe akoki kosala nini.',
    'sf.inviteColleague': 'Kobyanga moninga ya mosala',
    'sf.lastSignIn': 'Kokota ya nsuka',
    'sf.noStaff': 'Basali bazali naino te',
    'sf.inviteHint': 'Byanga baninga ya mosala mpo básalisa yo kokamba kɔntɛ oyo.',
    'sf.inviteSomeone': 'Kobyanga moto',
    'sf.sendInvitation': 'Kotinda libyangi',
    'bl.subtitle': 'Boyokani na yo na plateforme, mpe oyo ozali na niongo.',
    'bl.noInvoices': 'Bafaktire ezali naino te',
    'bl.invoiceHint': 'Plateforme ebimisaka faktire na ebandeli ya sanza nyonso.',
    'bl.yourContract': 'Kontra na yo',
    'bl.commissionPer': 'Komisiɔ na bokomisi moko',
    'bl.monthlyFee': 'Lifuti ya sanza',
    'bl.inEffect': 'Ezali kosala banda',
    'bl.noContract': 'Kontra moko ezali kosala te sikoyo. Benga lisungi ya plateforme.',
    'bl.thisMonth': 'Sanza oyo tii sikoyo',
    'bl.confirmed': 'Bokomisi endimami',
    'bl.gross': 'Mbongo mobimba',
    'bl.accrued': 'Komisiɔ esangani',
    'bl.estimated': 'Motuya ya kokanisa',
    'bl.refundNote': 'Bokomisi oyo ezongisami elongolaka komisiɔ na yango na sanza ya bozongisi.',
    'br.subtitle': 'Ndenge application na yo emonanaka na bapaya, na navigateur mpe soki ekotisami.',
    'br.appName': 'Nkombo ya application',
    'br.appNameHint': 'Balɛtrɛ 2–20. Yango nde nkombo na ekran ya ndako soki ekotisami.',
    'br.notReadable': 'Makomi ekoki kotangama te',
    'br.worthLook': 'Ebongi kotala',
    'br.themeMode': 'Lolenge ya thème',
    'br.preview': 'Botali ya application ya bapaya',
    'br.unpublished': 'Mbongwana oyo ebimisami te',
    'br.identity': 'Elembo',
    'br.maskable': 'Ikɔnɛ oyo ekoki kokatama — Android ekoki kokata yango na lolenge oyo ya libungutulu.',
    'br.fixBefore': 'Bongisá makambo oyo liboso ya kobimisa.',
    'br.canPublish': 'Okoki kaka kobimisa — oyo ezali kaka liyebisi ya botangi.',
    'br.typeTheme': 'Fɔntɛ mpe thème',
    'br.livePreview': 'Botali ya sikoyo',
    'br.customerFeatures': 'Ba fonctionnalités ya app ya bato',
    'br.customerFeaturesHint': 'Pona nini bato bakoki kosala na réservation na bango, mpe na molongo nini. Soki ozimi moko, ekomonana lisusu te na app.',
    'br.openApp': 'Kofungola application',
    'br.previewHint': 'Mbongwana ekomonana awa mbala moko. Bapaya bakomona yango soki obimisi.',
    'db.bookingsToday': 'Bokomisi ya lelo',
    'db.revenueToday': 'Mbongo ya lelo',
    'db.netRefunds': 'Nsima ya bozongisi',
    'db.revenueWeek': 'Mbongo ya pɔsɔ oyo',
    'db.openDisputes': 'Matata efungwami',
    'db.todaysDepartures': 'Mibembo ya lelo',
    'db.nothingToday': 'Eloko ebongisami te lelo.',
    'db.trendTitle': 'Bokeyi na sanza',
    'db.trendHint': 'Bokeyi endimami, sanza {months} eleki.',
    'db.trendSeries': 'Bokeyi na sanza',
    'db.revenueTrendTitle': 'Mbongo na sanza',
    'db.revenueTrendHint': 'Sima ya kozongisa mbongo, na {currency}.',
    'db.revenueSeries': 'Mbongo na sanza',
    'db.routesTitle': 'Banzela na kotonda',
    'db.routesHint': 'Bakiti etekami na bakiti epesamaki, mpo na mibembo esilaki kokende.',
    'db.window12': 'Sanza 12',
    'db.window24': 'Sanza 24',
    'db.noActivity': 'Mosala ezali te na eleko oyo',
    'db.seatsSold': 'Bakiti {count} etekami',
    'db.bookingsCount': 'Bokeyi {count}',
    'db.disputesWaiting': 'Ezali kozela eyano',
    'db.disputesNone': 'Eloko etikali te',
    'db.awaitingPayment': 'Bokeyi {count} ezali kozela lifuti. Bakiti ezongaka yango moko soki ntango ya kofuta esili.',
    'db.loadFactor': 'Etondi {percent}%',
    'ai.yourName': 'Nkombo na yo',
    'ai.min10': 'Ata balɛtrɛ 10.',
    'ai.setUp': 'Bongisá kɔntɛ na yo',
    'ai.setUpHint': 'Poná motindo ya kokota mpe okoti. Mokumba na yo epesami deja.',
    'ai.setAndSignIn': 'Kotia motindo mpe kokota',
    'brand.suffix': 'Mosala',
    'nav.section.today': 'Lelo',
    'nav.section.network': 'Nzela',
    'nav.section.business': 'Mombongo',
    'nav.section.admin': 'Bokambi',
    'nav.dashboard': 'Etanda ya bokambi',
    'nav.routes': 'Banzela',
    'nav.trips': 'Mibembo',
    'nav.bookings': 'Bokomisi',
    'nav.disputes': 'Matata',
    'nav.network': 'Bisika mpe banzela',
    'nav.fleet': 'Mituka',
    'nav.timetables': 'Bangonga',
    'nav.branding': 'Elembo',
    'nav.promotions': 'Promotions',
    'nav.settings': 'Ebongiseli',
    'nav.staff': 'Basali',
    'nav.roles': 'Mikumba',
    'nav.billing': 'Bafaktire',
    'shell.signOut': 'Kobima',
    'shell.language': 'Monɔkɔ',
    'shell.profile': 'Profil',
    'profile.title': 'Profil',
    'profile.heading': 'Profil na yo',
    'profile.hint': 'Nkombo na yo mpe ba contacts, mpe foto oyo baninga bazali komona pembeni ya nkombo na yo.',
    'profile.photo': 'Foto ya profil',
    'profile.upload': 'Tinda foto',
    'profile.replace': 'Bongola foto',
    'profile.removePhoto': 'Longola foto',
    'profile.photoHint': 'PNG, JPEG to WebP, tii na 2 MB. Carré ebimaka malamu.',
    'profile.name': 'Nkombo mobimba',
    'profile.phone': 'Telefone',
    'profile.phoneHint': 'Ezali na ntina makasi te. Baninga basalelaka yango mpo na kobenga yo.',
    'profile.email': 'Email',
    'profile.emailLocked': 'Email na yo nde okotelaka; administrateur nde abongolaka yango, awa te.',
    'profile.save': 'Bomba mbongwana',
    'profile.saved': 'Profil ebongwani.',
    'profile.saveFailed': 'Kobomba elongi te. Meka lisusu.',
    'profile.photoFailed': 'Foto wana etindami te.',
    'shell.openMenu': 'Kofungola menu',
    'shell.closeMenu': 'Kokanga menu',
    'common.loading': 'Ezali kofungwama…',
    'common.retry': 'Meka lisusu',
    'common.cancel': 'Kotika',
    'common.save': 'Kobomba',
    'common.close': 'Kokanga',
    'common.name': 'Nkombo',
    'common.email': 'Imeli',
    'notif.title': 'Esengeli otala',
    'notif.open': 'Kofungola bansango',
    'notif.empty': 'Eloko te ezali kozela yo sikoyo.',
    'notif.emptyHint': 'Matata oyo efungwami, mibembo oyo etekami te, mpe komata ya lelo bakomonana awa.',
    'notif.disputes': 'Likambo {count} efungwami',
    'notif.disputes_other': 'Makambo {count} efungwami',
    'notif.disputesHint': 'Mopaya azali kozela ekateli.',
    'notif.emptyTrips': 'Mobembo {count} ezali na bokomisi te',
    'notif.emptyTrips_other': 'Mibembo {count} ezali na bokomisi te',
    'notif.emptyTripsHint': 'Ekokende lelo mpe ezali naino mpamba.',
    'dash.title': 'Etanda ya bokambi',
    'dash.subtitle': 'Ndenge mosala na yo ezali kotambola.',
    'dash.today': 'Lelo',
    'dash.departuresToday': 'Mibembo ya lelo',
    'dash.seatsSold': 'Bakiti etekami',
    'dash.revenue': 'Mbongo',
    'dash.revenueHint': 'Nsima ya komisiɔ ya plateforme',
    'dash.load': 'Kotonda ya kati',
    'dash.loadHint': 'Na mibembo oyo esalemi',
    'routes.title': 'Mosala ya banzela',
    'routes.subtitle': 'Banzela nini ya yo ezali kosala malamu.',
    'routes.departed': 'Ekendeki',
    'routes.selling': 'Ezali kotekama',
    'routes.windowHint.departed': 'Mibembo esalemi, na mikolo {days} eleki. Kotonda ezali ya nsuka.',
    'routes.windowHint.selling': 'Mibembo ezali naino kotekama, na mikolo {days} ekoya. Kotonda ezali ndenge etondi tii sikoyo.',
    'routes.colRoute': 'Nzela',
    'routes.colDepartures': 'Mibembo',
    'routes.colSeats': 'Bakiti',
    'routes.colLoad': 'Kotonda',
    'routes.colGross': 'Mobimba',
    'routes.colNet': 'Otikali na yango',
    'routes.emptyTitle': 'Mobembo moko te na eleko oyo',
    'routes.emptyBody': 'Ntango mibembo esalemi, kotonda mpe mbongo na yango ekomonana awa.',
    'signIn.title': 'Kokota',
    'signIn.subtitle': 'Kɔntɛ na yo elakisi kompani nini ozali kokamba.',
    'signIn.email': 'Imeli',
    'signIn.password': 'Motindo ya kokota',
    'signIn.submit': 'Kokota',
  },

} as const;

/**
 * Every locale must carry every key, or a translator's omission is discovered by
 * whoever is reading the screen. This turns it into a compile error instead.
 */
const _everyLocaleIsComplete: Record<LocaleCode, Record<StringKey, string>> = dict;
void _everyLocaleIsComplete;
